import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Fab,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  LinearProgress,
  Snackbar,
} from '@mui/material';
import {
  Receipt,
  Add,
  Visibility,
  Send,
  Payment,
  Print,
  MoreVert,
  AttachMoney,
  PendingActions,
  CheckCircle,
  Warning,
  Close,
  History,
  Verified,
  Edit,
} from '@mui/icons-material';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { invoiceService, Invoice, InvoiceFilters, PaymentMethod, RecordPaymentRequest } from '../../services/invoiceService';
import PaymentHistoryTable from '../../components/sales/PaymentHistoryTable';
import InvoicePrintPage from './InvoicePrintPage';
import SalesInvoiceDetailPage from './SalesInvoiceDetailPage';
import InvoiceFormPage from './InvoiceFormPage';
import EInvoiceDialog from '../../components/sales/EInvoiceDialog';
import { RouterLink } from '../../utils/linkNav';
import ExportButton from '../../components/common/ExportButton';

interface InvoiceStats {
  totalInvoices: number;
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  overdueInvoices: number;
}

interface PaymentFormData {
  paymentAmount: string;
  paymentDate: string;
  paymentMethod: PaymentMethod | '';
  referenceNumber: string;
  notes: string;
}

const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CARD', label: 'Card' },
];

const InvoiceOverview: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [stats, setStats] = useState<InvoiceStats>({
    totalInvoices: 0,
    totalAmount: 0,
    paidAmount: 0,
    pendingAmount: 0,
    overdueInvoices: 0,
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<InvoiceFilters>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [einvoiceOpen, setEinvoiceOpen] = useState(false);
  const [einvoiceInvoice, setEinvoiceInvoice] = useState<Invoice | null>(null);

  // Payment Modal State
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null);
  const [paymentFormData, setPaymentFormData] = useState<PaymentFormData>({
    paymentAmount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    referenceNumber: '',
    notes: '',
  });
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // View Invoice Modal State
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewInvoice, setViewInvoice] = useState<Invoice | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Snackbar State
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await invoiceService.getInvoices(filters);
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await invoiceService.getInvoiceStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading invoice stats:', error);
    }
  };

  useEffect(() => {
    loadInvoices();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const handleUpdateStatus = async (status: string) => {
    if (!selectedInvoice) return;

    try {
      await invoiceService.updateInvoiceStatus(selectedInvoice.id, { status } as any);
      loadInvoices();
      handleMenuClose();
      setSnackbar({ open: true, message: `Invoice status updated to ${status}`, severity: 'success' });
    } catch (error) {
      console.error('Error updating invoice status:', error);
      setSnackbar({ open: true, message: 'Failed to update invoice status', severity: 'error' });
    }
  };

  const handleViewInvoice = async (invoice: Invoice) => {
    try {
      setViewLoading(true);
      setViewModalOpen(true);
      const fullInvoice = await invoiceService.getInvoiceById(invoice.id);
      setViewInvoice(fullInvoice);
    } catch (error) {
      console.error('Error loading invoice details:', error);
      setSnackbar({ open: true, message: 'Failed to load invoice details', severity: 'error' });
    } finally {
      setViewLoading(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewInvoice(null);
  };

  // Payment Modal Handlers
  const handleOpenPaymentModal = async (invoice: Invoice) => {
    try {
      const fullInvoice = await invoiceService.getInvoiceById(invoice.id);
      const balance = fullInvoice.totalAmount - fullInvoice.paidAmount;
      setPaymentInvoice(fullInvoice);
      setPaymentFormData({
        paymentAmount: balance.toFixed(2),
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: '',
        referenceNumber: '',
        notes: '',
      });
      setPaymentError(null);
      setPaymentModalOpen(true);
      handleMenuClose();
    } catch (error) {
      console.error('Error loading invoice for payment:', error);
      setSnackbar({ open: true, message: 'Failed to load invoice details', severity: 'error' });
    }
  };

  const handleClosePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentInvoice(null);
    setPaymentFormData({
      paymentAmount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: '',
      referenceNumber: '',
      notes: '',
    });
    setPaymentError(null);
  };

  const handlePaymentFormChange = (field: keyof PaymentFormData, value: string) => {
    setPaymentFormData((prev) => ({ ...prev, [field]: value }));
    setPaymentError(null);
  };

  const handleSubmitPayment = async () => {
    if (!paymentInvoice) return;

    // Validation
    const amount = parseFloat(paymentFormData.paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      setPaymentError('Please enter a valid payment amount');
      return;
    }

    const balance = paymentInvoice.totalAmount - paymentInvoice.paidAmount;
    if (amount > balance) {
      setPaymentError(`Payment amount cannot exceed balance of ${formatCurrency(balance)}`);
      return;
    }

    if (!paymentFormData.paymentDate) {
      setPaymentError('Please select a payment date');
      return;
    }

    if (!paymentFormData.paymentMethod) {
      setPaymentError('Please select a payment method');
      return;
    }

    // Reference number required for non-cash payments
    if (paymentFormData.paymentMethod !== 'CASH' && !paymentFormData.referenceNumber.trim()) {
      setPaymentError('Reference number is required for non-cash payments');
      return;
    }

    try {
      setPaymentSubmitting(true);

      const paymentData: RecordPaymentRequest = {
        paymentAmount: amount,
        paymentDate: paymentFormData.paymentDate,
        paymentMethod: paymentFormData.paymentMethod as PaymentMethod,
        referenceNumber: paymentFormData.referenceNumber.trim() || undefined,
        notes: paymentFormData.notes.trim() || undefined,
      };

      const result = await invoiceService.recordPayment(paymentInvoice.id, paymentData);

      setSnackbar({
        open: true,
        message: `Payment of ${formatCurrency(amount)} recorded successfully. Invoice is now ${result.newStatus}.`,
        severity: 'success',
      });

      handleClosePaymentModal();
      loadInvoices();
      loadStats();
    } catch (error: any) {
      console.error('Error recording payment:', error);
      setPaymentError(error.message || 'Failed to record payment');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'SENT':
        return 'primary';
      case 'DRAFT':
        return 'default';
      case 'OVERDUE':
        return 'error';
      case 'CANCELLED':
        return 'warning';
      case 'PARTIALLY_PAID':
        return 'info';
      default:
        return 'default';
    }
  };

  const getBalancePercentage = (invoice: Invoice) => {
    if (invoice.totalAmount === 0) return 0;
    return (invoice.paidAmount / invoice.totalAmount) * 100;
  };

  const quickStats = [
    {
      title: 'Total Invoices',
      value: stats.totalInvoices,
      formatted: stats.totalInvoices.toString(),
      icon: <Receipt />,
      color: 'primary',
    },
    {
      title: 'Total Amount',
      value: stats.totalAmount,
      formatted: formatIndianNumber(stats.totalAmount),
      icon: <AttachMoney />,
      color: 'success',
    },
    {
      title: 'Pending Amount',
      value: stats.pendingAmount,
      formatted: formatIndianNumber(stats.pendingAmount),
      icon: <PendingActions />,
      color: 'warning',
    },
    {
      title: 'Overdue Invoices',
      value: stats.overdueInvoices,
      formatted: stats.overdueInvoices.toString(),
      icon: <Warning />,
      color: 'error',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            GST Invoice Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage GST-compliant invoices with Indian tax calculations and compliance.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ExportButton
            rows={invoices as any}
            columns={[
              { header: 'Invoice #', key: 'invoiceNumber' }, { header: 'Customer', key: 'companyName' },
              { header: 'Date', key: 'invoiceDate' }, { header: 'Total', key: 'totalAmount' },
              { header: 'Paid', key: 'paidAmount' }, { header: 'Status', key: 'status' },
            ]}
            filename="sales-invoices"
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            component={RouterLink}
            to="/sales/invoices/new"
            sx={{ fontWeight: 'bold' }}
          >
            Create Invoice
          </Button>
        </Box>
      </Box>

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickStats.map((stat, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" color={`${stat.color}.main`} sx={{ fontWeight: 'bold' }}>
                      {stat.formatted}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                  <Box sx={{ color: `${stat.color}.main`, fontSize: 40 }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="SENT">Sent</MenuItem>
                  <MenuItem value="PARTIALLY_PAID">Partially Paid</MenuItem>
                  <MenuItem value="PAID">Paid</MenuItem>
                  <MenuItem value="OVERDUE">Overdue</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate || ''}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value || undefined })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={filters.toDate || ''}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value || undefined })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setFilters({})}
                sx={{ height: '40px' }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invoice List */}
      <Paper sx={{ mt: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Invoice No.</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Payment Progress</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography>Loading invoices...</Typography>
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No invoices found. Create your first invoice to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => {
                  const balance = invoice.totalAmount - invoice.paidAmount;
                  const paidPercentage = getBalancePercentage(invoice);

                  return (
                    <TableRow key={invoice.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {invoice.invoiceNumber}
                      </TableCell>
                      <TableCell>{invoice.companyName}</TableCell>
                      <TableCell>{new Date(invoice.invoiceDate).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {invoice.totalAmountFormatted || formatCurrency(invoice.totalAmount)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="success.main">
                              {formatCurrency(invoice.paidAmount)}
                            </Typography>
                            <Typography variant="caption" color="error.main">
                              {formatCurrency(balance)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={paidPercentage}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'error.100',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: paidPercentage === 100 ? 'success.main' : 'info.main',
                              },
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status.replace('_', ' ')}
                          size="small"
                          color={getStatusColor(invoice.status) as any}
                          variant={invoice.status === 'PARTIALLY_PAID' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewInvoice(invoice)}
                          title="View Invoice"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, invoice)}
                          title="More Options"
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { const id = selectedInvoice?.id; handleMenuClose(); if (id) navigate(`/sales/invoices/${id}`); }}>
          <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
          <ListItemText>Open Invoice</ListItemText>
        </MenuItem>
        {selectedInvoice?.status === 'DRAFT' && (
          <MenuItem onClick={() => { const id = selectedInvoice?.id; handleMenuClose(); if (id) navigate(`/sales/invoices/new?editId=${id}`); }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
            <ListItemText>Edit Invoice</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => selectedInvoice && handleUpdateStatus('SENT')}>
          <ListItemIcon><Send fontSize="small" /></ListItemIcon>
          <ListItemText>Mark as Sent</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedInvoice && handleUpdateStatus('PAID')}>
          <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
          <ListItemText>Mark as Paid</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => selectedInvoice && handleOpenPaymentModal(selectedInvoice)}
          disabled={selectedInvoice?.status === 'PAID' || selectedInvoice?.status === 'CANCELLED'}
        >
          <ListItemIcon><Payment fontSize="small" /></ListItemIcon>
          <ListItemText>Record Payment</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedInvoice && window.open(`/sales/invoices/${selectedInvoice.id}/print`, '_blank')}>
          <ListItemIcon><Print fontSize="small" /></ListItemIcon>
          <ListItemText>Print Invoice</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (selectedInvoice) { setEinvoiceInvoice(selectedInvoice); setEinvoiceOpen(true); } handleMenuClose(); }}>
          <ListItemIcon><Verified fontSize="small" /></ListItemIcon>
          <ListItemText>E-Invoice & E-Way (Demo)</ListItemText>
        </MenuItem>
      </Menu>

      <EInvoiceDialog
        open={einvoiceOpen}
        invoiceId={einvoiceInvoice ? einvoiceInvoice.id : null}
        invoiceNumber={einvoiceInvoice?.invoiceNumber}
        onClose={() => setEinvoiceOpen(false)}
      />

      {/* Payment Modal */}
      <Dialog
        open={paymentModalOpen}
        onClose={handleClosePaymentModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Payment color="primary" />
              <Typography variant="h6">Record Payment</Typography>
            </Box>
            <IconButton onClick={handleClosePaymentModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {paymentInvoice && (
            <>
              {/* Invoice Summary */}
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                        {paymentInvoice.invoiceNumber}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Customer</Typography>
                      <Typography variant="body1">{paymentInvoice.companyName}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(paymentInvoice.totalAmount)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary">Paid</Typography>
                      <Typography variant="body1" color="success.main">
                        {formatCurrency(paymentInvoice.paidAmount)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary">Balance</Typography>
                      <Typography variant="h6" color="error.main" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(paymentInvoice.totalAmount - paymentInvoice.paidAmount)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {paymentError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {paymentError}
                </Alert>
              )}

              {/* Payment Form */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={paymentFormData.paymentAmount}
                    onChange={(e) => handlePaymentFormChange('paymentAmount', e.target.value)}
                    inputProps={{
                      step: '0.01',
                      min: '0.01',
                      max: (paymentInvoice.totalAmount - paymentInvoice.paidAmount).toFixed(2),
                    }}
                    required
                    helperText={`Max: ${formatCurrency(paymentInvoice.totalAmount - paymentInvoice.paidAmount)}`}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Payment Date"
                    type="date"
                    value={paymentFormData.paymentDate}
                    onChange={(e) => handlePaymentFormChange('paymentDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={paymentFormData.paymentMethod}
                      label="Payment Method"
                      onChange={(e) => handlePaymentFormChange('paymentMethod', e.target.value)}
                    >
                      {PAYMENT_METHODS.map((method) => (
                        <MenuItem key={method.value} value={method.value}>
                          {method.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Reference Number"
                    value={paymentFormData.referenceNumber}
                    onChange={(e) => handlePaymentFormChange('referenceNumber', e.target.value)}
                    placeholder={
                      paymentFormData.paymentMethod === 'CHEQUE'
                        ? 'Cheque Number'
                        : paymentFormData.paymentMethod === 'BANK_TRANSFER'
                        ? 'UTR / Transaction ID'
                        : paymentFormData.paymentMethod === 'UPI'
                        ? 'UPI Transaction ID'
                        : paymentFormData.paymentMethod === 'CARD'
                        ? 'Transaction Reference'
                        : ''
                    }
                    required={paymentFormData.paymentMethod !== 'CASH' && paymentFormData.paymentMethod !== ''}
                    helperText={
                      paymentFormData.paymentMethod && paymentFormData.paymentMethod !== 'CASH'
                        ? 'Required for non-cash payments'
                        : 'Optional for cash payments'
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Notes (Optional)"
                    value={paymentFormData.notes}
                    onChange={(e) => handlePaymentFormChange('notes', e.target.value)}
                    multiline
                    rows={2}
                    placeholder="Any additional notes about this payment..."
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClosePaymentModal} disabled={paymentSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitPayment}
            disabled={paymentSubmitting}
            startIcon={paymentSubmitting ? <CircularProgress size={20} /> : <Payment />}
          >
            {paymentSubmitting ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Invoice Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt color="primary" />
              <Typography variant="h6">Invoice Details</Typography>
            </Box>
            <IconButton onClick={handleCloseViewModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : viewInvoice ? (
            <>
              {/* Invoice Header */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                        {viewInvoice.invoiceNumber}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Date</Typography>
                      <Typography variant="body1">
                        {new Date(viewInvoice.invoiceDate).toLocaleDateString('en-IN')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Due Date</Typography>
                      <Typography variant="body1">
                        {new Date(viewInvoice.dueDate).toLocaleDateString('en-IN')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Box>
                        <Chip
                          label={viewInvoice.status.replace('_', ' ')}
                          size="small"
                          color={getStatusColor(viewInvoice.status) as any}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="caption" color="text.secondary">Customer</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {viewInvoice.companyName}
                      </Typography>
                      {viewInvoice.customerGstin && (
                        <Typography variant="body2" color="text.secondary">
                          GSTIN: {viewInvoice.customerGstin}
                        </Typography>
                      )}
                    </Grid>
                    <Grid size={{ xs: 4, md: 2 }}>
                      <Typography variant="caption" color="text.secondary">Total</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(viewInvoice.totalAmount)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4, md: 2 }}>
                      <Typography variant="caption" color="text.secondary">Paid</Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(viewInvoice.paidAmount)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4, md: 2 }}>
                      <Typography variant="caption" color="text.secondary">Balance</Typography>
                      <Typography variant="h6" color="error.main" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(viewInvoice.totalAmount - viewInvoice.paidAmount)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Payment History */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <History color="primary" />
                  <Typography variant="h6">Payment History</Typography>
                </Box>
                <PaymentHistoryTable
                  payments={viewInvoice.payments || []}
                  emptyMessage="No payments recorded for this invoice"
                />
              </Box>

              {/* Action Buttons */}
              {viewInvoice.status !== 'PAID' && viewInvoice.status !== 'CANCELLED' && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Payment />}
                    onClick={() => {
                      handleCloseViewModal();
                      handleOpenPaymentModal(viewInvoice);
                    }}
                  >
                    Record Payment
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography color="error">Failed to load invoice details</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewModal}>Close</Button>
          {viewInvoice && (
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={() => window.open(`/sales/invoices/${viewInvoice.id}/print`, '_blank')}
            >
              Print Invoice
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* GST Compliance Alert */}
      <Alert
        severity="info"
        sx={{ mt: 3, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}
        icon={<Receipt />}
      >
        <strong>GST Compliance:</strong> All invoices follow Indian GST format with proper GSTIN, HSN codes,
        and tax calculations. Invoice numbers follow FY-based sequential format (INV/FY2026-27/0001).
      </Alert>

      {/* Floating Action Button — same destination as the header's Create
          Invoice button: the dedicated Invoice Workbench (InvoiceFormPage).
          The old QuickCreateInvoice dialog submitted a fabricated
          `productId: index+1` for every line, which is a data-integrity bug;
          it has been removed as a creation entry point in favour of the
          workbench's real product autocomplete. */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => navigate('/sales/invoices/new')}
      >
        <Add />
      </Fab>
    </Box>
  );
};

const InvoicePage: React.FC = () => {
  return (
    <Routes>
      <Route index element={<InvoiceOverview />} />
      <Route path="new" element={<InvoiceFormPage />} />
      <Route path=":id" element={<SalesInvoiceDetailPage />} />
      <Route path=":id/print" element={<InvoicePrintPage />} />
    </Routes>
  );
};

export default InvoicePage;
